import { useRef, useState, useEffect, useCallback, memo } from "react";
import SignatureCanvas from "react-signature-canvas";
import { jsPDF } from "jspdf";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Eraser, PenLine, FileSignature, ShieldCheck, GraduationCap } from "lucide-react";

const SignaturePad = memo(function SignaturePad({ onChange }) {
  const ref = useRef(null);
  return (
    <Card className="p-4 bg-white border-border">
      <div className="flex items-center justify-between mb-2">
        <Label className="flex items-center gap-2 text-primary"><PenLine size={16} /> Firma digitale (col dito)</Label>
        <Button type="button" variant="ghost" size="sm" data-testid="clear-signature" onClick={() => { ref.current?.clear(); onChange(""); }}><Eraser size={14} className="mr-1" /> Cancella</Button>
      </div>
      <div className="rounded-md bg-white overflow-hidden">
        <SignatureCanvas ref={ref} penColor="#111" canvasProps={{ className: "sig-canvas" }}
          onEnd={() => onChange(ref.current.getCanvas().toDataURL("image/png"))} />
      </div>
    </Card>
  );
});

export default function EnrollmentForm({ userName, year, courses = [], onDone }) {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({});
  const [course, setCourse] = useState("");
  const [note, setNote] = useState("");
  const [typedName, setTypedName] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [signatureImg, setSignatureImg] = useState("");
  const [busy, setBusy] = useState(false);

  const onSignatureChange = useCallback((d) => setSignatureImg(d), []);

  useEffect(() => {
    api.get("/enrollment-config").then((r) => {
      setConfig(r.data);
      const init = {};
      (r.data.fields || []).forEach((f, i) => {
        if (f.label.toLowerCase().includes("nome") && userName) init[i] = userName;
      });
      setForm(init);
    }).catch(() => {});
  }, [userName]);

  const compressSignature = (dataUrl) =>
    new Promise((resolve) => {
      if (!dataUrl) return resolve(dataUrl);
      const img = new Image();
      img.onload = () => {
        const maxW = 600;
        const scale = Math.min(1, maxW / (img.width || maxW));
        const w = Math.max(1, Math.round((img.width || maxW) * scale));
        const h = Math.max(1, Math.round((img.height || 200) * scale));
        const c = document.createElement("canvas");
        c.width = w; c.height = h;
        const ctx = c.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        try { resolve(c.toDataURL("image/jpeg", 0.7)); }
        catch { resolve(dataUrl); }
      };
      img.onerror = () => resolve(dataUrl);
      img.src = dataUrl;
    });

  const buildPdf = (signatureImg) => {
    const doc = new jsPDF({ compress: true });
    const pageH = doc.internal.pageSize.getHeight();
    let y = 36;
    const ensure = (h) => { if (y + h > pageH - 15) { doc.addPage(); y = 20; } };

    doc.setFillColor(10, 10, 10); doc.rect(0, 0, 210, 26, "F");
    doc.setTextColor(236, 28, 100); doc.setFontSize(18); doc.text("Areté Teatro", 14, 17);
    doc.setTextColor(0, 0, 0);

    doc.setFontSize(14); doc.text(config.title || "Modulo di Iscrizione", 14, y); y += 9;

    if (config.body) {
      doc.setFontSize(10);
      doc.splitTextToSize(config.body, 182).forEach((line) => { ensure(6); doc.text(line, 14, y); y += 5.5; });
      y += 4;
    }

    doc.setFontSize(11);
    (config.fields || []).forEach((f, i) => {
      ensure(8);
      doc.setFont(undefined, "bold"); doc.text(`${f.label}:`, 14, y);
      doc.setFont(undefined, "normal");
      const v = doc.splitTextToSize(String(form[i] || "-"), 95);
      doc.text(v, 95, y);
      y += Math.max(7, v.length * 5.5);
    });
    y += 4;

    if (note && note.trim()) {
      doc.setFont(undefined, "bold"); ensure(7); doc.text("Note:", 14, y);
      doc.setFont(undefined, "normal");
      doc.splitTextToSize(note.trim(), 150).forEach((line, idx) => { ensure(6); doc.text(line, idx === 0 ? 30 : 14, y); y += 5.5; });
      y += 3;
    }

    doc.setFontSize(10);
    ensure(7); doc.text("[X] Dichiaro di accettare il modulo di iscrizione e il regolamento.", 14, y); y += 6;
    ensure(7); doc.text("[X] Ho letto l'informativa privacy e acconsento al trattamento dei dati (GDPR).", 14, y); y += 10;

    ensure(34);
    doc.setFont(undefined, "bold"); doc.text("Firma:", 14, y);
    if (signatureImg) doc.addImage(signatureImg, "JPEG", 40, y - 10, 60, 22);
    doc.setFont(undefined, "normal");
    doc.text(`Firmato da: ${typedName}`, 14, y + 20);
    doc.text(`Data: ${new Date().toLocaleString("it-IT")}`, 14, y + 28);

    if (config.privacy_text) {
      doc.addPage(); y = 20;
      doc.setFontSize(12); doc.text("Informativa Privacy (GDPR)", 14, y); y += 8;
      doc.setFontSize(9);
      doc.splitTextToSize(config.privacy_text, 182).forEach((line) => { ensure(5); doc.text(line, 14, y); y += 4.5; });
    }
    return doc.output("datauristring");
  };

  const submit = async () => {
    for (let i = 0; i < (config.fields || []).length; i++) {
      const f = config.fields[i];
      if (f.required && !form[i]) return toast.error(`Compila: ${f.label}`);
    }
    if (!typedName.trim()) return toast.error("Digita nome e cognome per confermare la firma");
    if (!accepted) return toast.error("Devi accettare il modulo e il regolamento");
    if (!privacy) return toast.error("Devi acconsentire al trattamento dei dati (privacy)");
    if (!signatureImg) return toast.error("Disegna la tua firma nel riquadro");
    setBusy(true);
    try {
      const formData = {};
      (config.fields || []).forEach((f, i) => { formData[f.label] = form[i] || ""; });
      const compactSig = await compressSignature(signatureImg);
      const signedPdf = buildPdf(compactSig);
      const { data } = await api.post("/enrollment/submit-form", {
        form_data: formData, signature: compactSig, typed_name: typedName,
        accepted_terms: accepted, privacy_consent: privacy, signed_pdf: signedPdf,
        note: note || "",
      }, { timeout: 60000 });
      toast.success("Modulo firmato e inviato!");
      onDone(data);
    } catch (err) {
      if (err.code === "ECONNABORTED" || !err.response) {
        toast.error("Invio non riuscito: la connessione ha impiegato troppo tempo o è caduta. Controlla la rete e riprova.");
      } else {
        toast.error(apiError(err.response?.data?.detail));
      }
    }
    finally { setBusy(false); }
  };

  if (!config) return <p className="text-muted-foreground">Caricamento modulo…</p>;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-2xl mb-2">{config.title}</h3>
        {config.body && <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed">{config.body}</p>}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {(config.fields || []).map((f, i) => (
          <div key={i}>
            <Label className="text-xs">{f.label}{f.required && " *"}</Label>
            <Input data-testid={`enr-field-${i}`} value={form[i] || ""}
              onChange={(e) => setForm({ ...form, [i]: e.target.value })}
              className="mt-1.5 bg-white" />
          </div>
        ))}
      </div>

      {courses.length >= 0 && (
        <Card className="p-4 bg-white border-border">
          <Label className="flex items-center gap-2 text-primary mb-2"><PenLine size={16} /> Note (facoltativo)</Label>
          <Textarea data-testid="enr-note" value={note} onChange={(e) => setNote(e.target.value)} className="bg-white min-h-24" placeholder="Hai qualcosa da comunicare alla direzione? Scrivilo qui (facoltativo)." />
          <p className="text-xs text-muted-foreground mt-2">Il corso verrà assegnato dalla direzione.</p>
        </Card>
      )}

      {config.privacy_text && (
        <Card className="p-4 bg-white border-border">
          <Label className="flex items-center gap-2 text-primary mb-2"><ShieldCheck size={16} /> Informativa Privacy (GDPR)</Label>
          <div className="text-xs text-muted-foreground whitespace-pre-line max-h-40 overflow-y-auto pr-2 leading-relaxed" data-testid="privacy-text">{config.privacy_text}</div>
        </Card>
      )}

      <div>
        <Label className="text-xs">Conferma firma digitando Nome e Cognome *</Label>
        <Input data-testid="typed-name" value={typedName} onChange={(e) => setTypedName(e.target.value)} className="mt-1.5 bg-white" placeholder="Es. Mario Rossi" />
      </div>

      <label className="flex items-start gap-3 cursor-pointer">
        <Checkbox checked={accepted} onCheckedChange={setAccepted} data-testid="accept-terms" className="mt-0.5" />
        <span className="text-sm text-muted-foreground">Dichiaro di aver letto e di accettare il modulo di iscrizione e il regolamento di Areté Teatro.</span>
      </label>

      <label className="flex items-start gap-3 cursor-pointer">
        <Checkbox checked={privacy} onCheckedChange={setPrivacy} data-testid="accept-privacy" className="mt-0.5" />
        <span className="text-sm text-muted-foreground">Ho letto l'informativa privacy e acconsento al trattamento dei miei dati personali (Reg. UE 2016/679 - GDPR).</span>
      </label>

      <div className="rounded-lg bg-primary/5 border border-primary/20 p-3 text-sm text-primary">
        Ultimo passaggio: <strong>firma qui sotto</strong> dopo aver compilato i campi e spuntato le caselle.
      </div>

      <SignaturePad onChange={onSignatureChange} />

      <Button data-testid="submit-enrollment" onClick={submit} disabled={busy} className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
        <FileSignature size={18} className="mr-2" /> {busy ? "Invio…" : "Accetta, firma e invia il modulo"}
      </Button>
    </div>
  );
}
